import { useState, useEffect, useRef } from "react";
import { trackOnce } from "./analytics";
import { isPro } from "./Pro";
import { C, CURRENCIES, todayStr, FontLink, BUDGETS_KEY, save } from "./constants";


// ── BUDGET ALLOCATIONS ────────────────────────────────────────
const DEFAULT_ALLOCS = [
  { cat:"Rent",          pct:30, icon:"🏠", tip:"Housing — keep under 30%" },
  { cat:"Groceries",     pct:12, icon:"🛒", tip:"Groceries & household items" },
  { cat:"Food",          pct:8,  icon:"🍔", tip:"Eating out & takeaway" },
  { cat:"Transport",     pct:10, icon:"🚗", tip:"Car, fuel, transit" },
  { cat:"Utilities",     pct:5,  icon:"💡", tip:"Electricity, water, internet" },
  { cat:"Phone",         pct:3,  icon:"📱", tip:"Phone plan" },
  { cat:"Subscriptions", pct:2,  icon:"📺", tip:"Netflix, Spotify, etc." },
  { cat:"Health",        pct:4,  icon:"💊", tip:"Medical, pharmacy, gym" },
  { cat:"Education",     pct:3,  icon:"🎓", tip:"Courses, books, school" },
  { cat:"Shopping",      pct:4,  icon:"🛍️", tip:"Clothes, household goods" },
  { cat:"Sadaqah",       pct:5,  icon:"🤲", tip:"Charity — recommended 2.5–10%" },
  { cat:"Savings",       pct:10, icon:"💰", tip:"Emergency fund & goals" },
  { cat:"Other",         pct:4,  icon:"📋", tip:"Everything else" },
];

function calcBudgets(income, allocs) {
  const result = {};
  allocs.forEach(a => { result[a.cat] = Math.round((income * a.pct) / 100); });
  return result;
}

// ── ANIMATED COUNTER ──────────────────────────────────────────
function Counter({ target, duration = 800, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(target * ease));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return <>{prefix}{val.toLocaleString()}{suffix}</>;
}

// ── ONBOARDING ────────────────────────────────────────────────
function Onboarding({ onComplete }) {
  const [step, setStep]         = useState(0);
  const [currency, setCurrency] = useState("CAD");
  const [name, setName]         = useState("");
  const [income, setIncome]     = useState("");
  const [rent, setRent]         = useState("");
  const [food, setFood]         = useState("");
  const [revealed, setRevealed] = useState(false);

  const incomeNum = parseFloat(income) || 0;
  const rentTyped = rent.trim() !== "";
  const foodTyped = food.trim() !== "";
  const rentNum   = rentTyped ? (parseFloat(rent) || 0) : Math.round(incomeNum * 0.32);
  const foodNum   = foodTyped ? (parseFloat(food) || 0) : Math.round(incomeNum * 0.14);
  const sym = CURRENCIES.find(c => c.code === currency)?.symbol || "$";
  const firstName = name.split(" ")[0] || "you";

  useEffect(() => {
    if (step === 3) {
      setRevealed(false);
      const t = setTimeout(() => setRevealed(true), 300);
      return () => clearTimeout(t);
    }
  }, [step]);

  const finish = () => {
    trackOnce("onboarding_completed");
    const cur = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
    const budgets = calcBudgets(incomeNum, DEFAULT_ALLOCS);
    save(BUDGETS_KEY, { income: incomeNum, allocs: DEFAULT_ALLOCS, budgets });
    localStorage.setItem("bb_onboard_done_at", Date.now().toString());
    onComplete({ name: name.trim() || "Friend", currency, currencySymbol: cur.symbol, monthlyIncome: incomeNum });
  };

  // --- Money math ---
  const transport    = Math.round(incomeNum * 0.10);
  const phoneSubs    = Math.round(incomeNum * 0.04);
  const shopping     = Math.round(incomeNum * 0.08);
  const accounted    = rentNum + foodNum + transport + phoneSubs + shopping;
  const disappeared  = Math.max(0, incomeNum - accounted);
  const couldSave    = Math.round(incomeNum * 0.10);
  const yearSavings  = couldSave * 12;
  const hajjYears    = yearSavings > 0 ? Math.ceil(8000 / yearSavings) : null;

  const inputStyle = (active) => ({
    width:"100%", boxSizing:"border-box", background:C.surface2,
    border:`1.5px solid ${active ? C.green : "rgba(255,255,255,0.1)"}`,
    borderRadius:14, color:C.text, fontSize:22, fontWeight:700,
    padding:"14px 16px 14px 64px", outline:"none", transition:"border 0.2s",
    fontFamily:"'DM Sans',sans-serif",
  });

  const btnPrimary = (enabled) => ({
    width:"100%", background: enabled ? `linear-gradient(135deg,${C.green},#22c55e)` : C.surface3,
    border:"none", borderRadius:14, color: enabled ? "#0e0e13" : C.text3,
    fontSize:15, fontWeight:800, padding:"16px", cursor: enabled ? "pointer" : "default",
    boxShadow: enabled ? "0 4px 20px rgba(61,214,140,0.25)" : "none", transition:"all 0.2s",
  });

  const btnBack = {
    width:"100%", background:"none", border:"none", color:C.text3,
    fontSize:12, padding:"12px", cursor:"pointer", marginTop:4,
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center", padding:"24px 20px",
      fontFamily:"'DM Sans',sans-serif" }}>
      <FontLink />

      {/* Progress bar */}
      {step > 0 && step < 5 && (
        <div style={{ width:"100%", maxWidth:420, marginBottom:28 }}>
          <div style={{ height:3, background:C.surface2, borderRadius:99, overflow:"hidden" }}>
            <div style={{ height:"100%", borderRadius:99, background:`linear-gradient(90deg,${C.green},#22c55e)`,
              width:`${(step / 5) * 100}%`, transition:"width 0.4s ease" }} />
          </div>
          <div style={{ color:C.text3, fontSize:10, textAlign:"right", marginTop:4 }}>Step {step} of 5</div>
        </div>
      )}

      <div style={{ width:"100%", maxWidth:420 }} key={step}>

        {/* ── STEP 0: Welcome ── */}
        {step === 0 && (
          <div style={{ animation:"fadeUp 0.35s ease", textAlign:"center" }}>
            <div style={{ fontSize:72, marginBottom:20 }}>🌙</div>
            <div style={{ fontSize:30, fontWeight:800, color:C.text, letterSpacing:"-0.03em",
              lineHeight:1.15, marginBottom:10 }}>
              Assalamu Alaikum
            </div>
            <div style={{ fontSize:15, color:C.text2, marginBottom:8, fontWeight:600 }}>
              Welcome to <span style={{color:C.green}}>Barakah Budget</span>
            </div>
            <div style={{ fontSize:13, color:C.text3, lineHeight:1.8, marginBottom:16, padding:"0 8px" }}>
              The halal-first finance tracker for Muslims in the West.
            </div>
            <div style={{ display:"flex", justifyContent:"center", gap:20, marginBottom:36 }}>
              {[["🔒","Private"],["📵","No ads"],["🕌","Halal-first"]].map(([e,l]) => (
                <div key={l} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:20, marginBottom:4 }}>{e}</div>
                  <div style={{ color:C.text3, fontSize:11 }}>{l}</div>
                </div>
              ))}
            </div>
            <button onClick={() => { trackOnce("onboarding_started"); setStep(1); }} style={btnPrimary(true)}>
              Get Started →
            </button>
          </div>
        )}

        {/* ── STEP 1: Name + Currency ── */}
        {step === 1 && (
          <div style={{ animation:"fadeUp 0.35s ease" }}>
            <div style={{ fontSize:42, textAlign:"center", marginBottom:16 }}>👋</div>
            <div style={{ fontSize:24, fontWeight:800, color:C.text, textAlign:"center",
              letterSpacing:"-0.02em", marginBottom:4 }}>What should we call you?</div>
            <div style={{ fontSize:12, color:C.text3, textAlign:"center", marginBottom:24 }}>
              And pick your currency
            </div>

            <div style={{ marginBottom:16 }}>
              <input type="text" placeholder="Your first name" value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && name.trim() && setStep(2)}
                style={{ width:"100%", boxSizing:"border-box", background:C.surface2,
                  border:`1.5px solid ${name.trim() ? C.green : "rgba(255,255,255,0.1)"}`,
                  borderRadius:14, color:C.text, fontSize:22, fontWeight:700,
                  padding:"16px 18px", outline:"none", transition:"border 0.2s",
                  fontFamily:"'DM Sans',sans-serif" }} />
            </div>

            <div style={{ position:"relative", marginBottom:28 }}>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ width:"100%", boxSizing:"border-box", background:C.surface2,
                  border:`1.5px solid rgba(255,255,255,0.1)`,
                  borderRadius:14, color:C.text, fontSize:16, fontWeight:700,
                  padding:"14px 44px 14px 16px", outline:"none", cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif", appearance:"none" }}>
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</option>
                ))}
              </select>
              <div style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)",
                color:C.text3, pointerEvents:"none", fontSize:12 }}>▼</div>
            </div>

            <button onClick={() => name.trim() && setStep(2)} style={btnPrimary(!!name.trim())}>
              Continue →
            </button>
            <button onClick={() => setStep(0)} style={btnBack}>← Back</button>
          </div>
        )}

        {/* ── STEP 2: 3 Questions ── */}
        {step === 2 && (
          <div style={{ animation:"fadeUp 0.35s ease" }}>
            <div style={{ fontSize:13, color:C.green, fontWeight:700, textTransform:"uppercase",
              letterSpacing:"0.08em", textAlign:"center", marginBottom:8 }}>3 quick questions</div>
            <div style={{ fontSize:24, fontWeight:800, color:C.text, textAlign:"center",
              letterSpacing:"-0.02em", marginBottom:4, lineHeight:1.2 }}>
              Let's build your<br/>money picture, {firstName}
            </div>
            <div style={{ fontSize:12, color:C.text3, textAlign:"center", marginBottom:28 }}>
              Estimates are fine — you can always update later
            </div>

            {/* Q1: Income */}
            <div style={{ marginBottom:16 }}>
              <div style={{ color:C.text3, fontSize:11, fontWeight:600, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:8 }}>💰 Monthly take-home pay (optional — helps us personalize)</div>
              <div style={{ position:"relative" }}>
                <div style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
                  color:C.text3, fontSize:18, fontWeight:700, pointerEvents:"none" }}>{sym}</div>
                <input type="number" inputMode="decimal" placeholder="0" value={income}
                  onChange={e => setIncome(e.target.value)}
                  style={inputStyle(incomeNum > 0)} />
              </div>
            </div>

            {/* Q2: Rent */}
            <div style={{ marginBottom:16 }}>
              <div style={{ color:C.text3, fontSize:11, fontWeight:600, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:8 }}>🏠 Monthly rent or mortgage</div>
              <div style={{ position:"relative" }}>
                <div style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
                  color:C.text3, fontSize:18, fontWeight:700, pointerEvents:"none" }}>{sym}</div>
                <input type="number" inputMode="decimal" placeholder={incomeNum > 0 ? Math.round(incomeNum*0.32).toString() : "0"} value={rent}
                  onChange={e => setRent(e.target.value)}
                  style={inputStyle(rentNum > 0)} />
              </div>
            </div>

            {/* Q3: Food */}
            <div style={{ marginBottom:28 }}>
              <div style={{ color:C.text3, fontSize:11, fontWeight:600, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:8 }}>🍔 Monthly food (groceries + eating out)</div>
              <div style={{ position:"relative" }}>
                <div style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
                  color:C.text3, fontSize:18, fontWeight:700, pointerEvents:"none" }}>{sym}</div>
                <input type="number" inputMode="decimal" placeholder={incomeNum > 0 ? Math.round(incomeNum*0.14).toString() : "0"} value={food}
                  onChange={e => setFood(e.target.value)}
                  style={inputStyle(foodNum > 0)} />
              </div>
            </div>

            <button onClick={() => setStep(incomeNum > 0 ? 3 : 5)} style={btnPrimary(true)}>
              Show me where my money goes →
            </button>
            <button onClick={() => setStep(1)} style={btnBack}>← Back</button>
          </div>
        )}

        {/* ── STEP 3: The Aha Moment ── */}
        {step === 3 && (
          <div style={{ animation:"fadeUp 0.35s ease" }}>
            <div style={{ fontSize:13, color:C.amber, fontWeight:700, textAlign:"center",
              textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>
              Your money reality check
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:C.text, textAlign:"center",
              letterSpacing:"-0.02em", marginBottom:4, lineHeight:1.2 }}>
              Here's what happens to<br/>{sym}{incomeNum.toLocaleString()} every month
            </div>
            <div style={{ fontSize:11, color:C.text3, textAlign:"center", marginBottom:20 }}>
              Based on what you told us
            </div>

            {/* Spending rows */}
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
              {[
                { label:"🏠 Rent / Mortgage",  amt:rentNum,   color:C.blue,  note:"your number",    known:rentTyped },
                { label:"🍔 Food & groceries",  amt:foodNum,   color:C.amber, note:"your number",    known:foodTyped },
                { label:"🚗 Transport",         amt:transport, color:C.text2, note:"estimated",      known:false },
                { label:"📱 Phone & subscriptions", amt:phoneSubs, color:C.text2, note:"estimated", known:false },
                { label:"🛍️ Shopping & misc",   amt:shopping,  color:C.red,   note:"estimated",     known:false },
              ].map((row, i) => (
                <div key={row.label} style={{
                  background:C.surface2,
                  border:`1px solid ${row.known ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.05)"}`,
                  borderRadius:12, padding:"10px 14px",
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? "translateY(0)" : "translateY(10px)",
                  transition:`all 0.35s ease ${i * 70}ms`,
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ color:C.text, fontSize:13 }}>{row.label}</div>
                      <div style={{ color:row.known ? C.blue : C.text3, fontSize:10, marginTop:1 }}>
                        {row.known ? "✓ your number" : "~ estimated"}
                      </div>
                    </div>
                    <div style={{ color:row.color, fontWeight:800, fontSize:15 }}>
                      {revealed ? <Counter target={row.amt} prefix={sym} duration={500} /> : `${sym}0`}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* The disappeared money — the real hook */}
            {revealed && disappeared > 0 && (
              <div style={{
                background:"linear-gradient(135deg,rgba(240,82,82,0.12),rgba(240,82,82,0.06))",
                border:"1.5px solid rgba(240,82,82,0.3)",
                borderRadius:14, padding:"16px 18px", marginBottom:16,
                animation:"fadeUp 0.5s ease 0.45s both",
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div style={{ color:"#f87171", fontWeight:700, fontSize:13 }}>😶 Unaccounted money</div>
                  <div style={{ color:C.red, fontWeight:800, fontSize:22 }}>
                    <Counter target={disappeared} prefix={sym} duration={700} />
                  </div>
                </div>
                <div style={{ color:C.text2, fontSize:12, lineHeight:1.6 }}>
                  After your known expenses, <strong style={{color:C.text}}>{sym}{disappeared.toLocaleString()}/month</strong> has no explanation.
                  That's <strong style={{color:"#f87171"}}>{sym}{(disappeared * 12).toLocaleString()} a year</strong> that just… disappears.
                </div>
              </div>
            )}

            {/* The upside */}
            {revealed && couldSave > 0 && (
              <div style={{
                background:"linear-gradient(135deg,rgba(61,214,140,0.1),rgba(61,214,140,0.04))",
                border:"1px solid rgba(61,214,140,0.25)",
                borderRadius:14, padding:"14px 16px", marginBottom:20,
                animation:"fadeUp 0.5s ease 0.65s both",
              }}>
                <div style={{ color:C.green, fontWeight:700, fontSize:12, marginBottom:6 }}>
                  💡 What if you found half of it?
                </div>
                <div style={{ color:C.text2, fontSize:13, lineHeight:1.6 }}>
                  Recovering just <strong style={{color:C.text}}>{sym}{Math.round(disappeared/2).toLocaleString()}/mo</strong> gives you{" "}
                  <strong style={{color:C.green}}>{sym}{(Math.round(disappeared/2)*12).toLocaleString()} this year</strong>.
                  {hajjYears && hajjYears <= 6 && (
                    <> That's <strong style={{color:C.gold}}>Hajj in {hajjYears} year{hajjYears>1?"s":""}</strong> 🕋</>
                  )}
                </div>
                <div style={{ color:C.text3, fontSize:11, marginTop:6 }}>
                  Barakah Budget gives every dollar a job — you'll see exactly which category ate your money and what's left to save.
                </div>
              </div>
            )}

            <button onClick={() => setStep(5)} style={btnPrimary(true)}>
              I want to find it →
            </button>
            <button onClick={() => setStep(2)} style={btnBack}>← Back</button>
          </div>
        )}

        {/* ── STEP 5: First Job ── */}
        {step === 5 && (
          <div style={{ animation:"fadeUp 0.35s ease", textAlign:"center" }}>
            <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
            <div style={{ fontSize:26, fontWeight:800, color:C.text, letterSpacing:"-0.03em",
              lineHeight:1.2, marginBottom:10 }}>
              You're set up, {firstName}
            </div>
            <div style={{ fontSize:13, color:C.text2, lineHeight:1.7, marginBottom:28, padding:"0 8px" }}>
              Your budget is ready. Now give the app something to work with.
            </div>

            {/* The single job */}
            <div style={{ background:C.surface2, border:`1.5px solid ${C.green}33`,
              borderRadius:16, padding:"20px", marginBottom:24, textAlign:"left" }}>
              <div style={{ fontSize:13, color:C.green, fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.08em", marginBottom:12 }}>Your first job</div>
              {[
                { n:1, text:"Tap the + button", sub:"Bottom right of the home screen" },
                { n:2, text:"Add your last 3 purchases", sub:"Takeout, groceries, transport — anything recent" },
                { n:3, text:"See where your money went", sub:"The picture builds with every transaction" },
              ].map(s => (
                <div key={s.n} style={{ display:"flex", gap:14, marginBottom:14, alignItems:"flex-start" }}>
                  <div style={{ width:28, height:28, borderRadius:50, background:`rgba(61,214,140,0.15)`,
                    border:`1.5px solid ${C.green}44`, display:"flex", alignItems:"center", justifyContent:"center",
                    color:C.green, fontWeight:800, fontSize:13, flexShrink:0 }}>{s.n}</div>
                  <div>
                    <div style={{ color:C.text, fontWeight:700, fontSize:14 }}>{s.text}</div>
                    <div style={{ color:C.text3, fontSize:11, marginTop:2 }}>{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* How it works — 3 icons */}
            <div style={{ display:"flex", gap:8, marginBottom:20 }}>
              {[
                { icon:"📂", title:"Every transaction", sub:"gets a category" },
                { icon:"📊", title:"See which bucket", sub:"is over or under" },
                { icon:"💰", title:"What's left", sub:"is yours to save" },
              ].map(s => (
                <div key={s.title} style={{ flex:1, background:C.surface2,
                  border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, padding:"12px 8px", textAlign:"center" }}>
                  <div style={{ fontSize:22, marginBottom:6 }}>{s.icon}</div>
                  <div style={{ color:C.text, fontSize:11, fontWeight:700, marginBottom:2 }}>{s.title}</div>
                  <div style={{ color:C.text3, fontSize:10, lineHeight:1.4 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ color:C.text3, fontSize:11, marginBottom:12, lineHeight:1.6 }}>
              بسم الله — In the name of Allah.<br/>May this bring barakah to your wealth. 🤲
            </div>

            {incomeNum > 0 && (
              <div style={{ background:"rgba(61,214,140,0.07)", border:"1px solid rgba(61,214,140,0.2)",
                borderRadius:10, padding:"10px 14px", marginBottom:16,
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ color:C.green, fontSize:12, fontWeight:700 }}>✓ Budget set automatically</div>
                  <div style={{ color:C.text3, fontSize:10, marginTop:2 }}>Based on your income · Adjust anytime in Wealth</div>
                </div>
                <div style={{ color:C.green, fontSize:18 }}>🎯</div>
              </div>
            )}

            <button onClick={finish}
              style={{ width:"100%", background:`linear-gradient(135deg,${C.green},#22c55e)`,
                border:"none", borderRadius:14, color:"#0e0e13", fontSize:16, fontWeight:800,
                padding:"18px", cursor:"pointer", boxShadow:"0 4px 24px rgba(61,214,140,0.3)",
                letterSpacing:"-0.01em" }}>
              Let's go — بسم الله →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// ── EXPORT / IMPORT ────────────────────────────────────────────
function ExportImport({ transactions, debtState, recurring, goals, zakatData, netWorthData, onImport }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const doExport = () => {
    const data = {
      version: 1,
      exportedAt: new Date().toISOString(),
      transactions, debtState, recurring, goals, zakatData, netWorthData
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `barakah-budget-backup-${todayStr()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowMenu(false);
  };

  const doImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.version === 1 && data.transactions) {
          onImport(data);
          alert("✅ Backup restored successfully!");
        } else {
          alert("❌ Invalid backup file.");
        }
      } catch { alert("❌ Could not read file."); }
      setImporting(false);
      setShowMenu(false);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div style={{ position:"relative" }}>
      <button onClick={() => setShowMenu(p=>!p)}
        style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", color:C.text3, borderRadius:8,
          padding:"6px 10px", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", letterSpacing:"2px", lineHeight:1 }}>
        ···
      </button>
      {showMenu && (
        <>
          <div onClick={() => setShowMenu(false)} style={{ position:"fixed", inset:0, zIndex:198 }} />
          <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, background:C.surface, border:`1px solid ${C.border2}`,
            borderRadius:12, padding:6, zIndex:199, minWidth:170, boxShadow:"0 8px 24px rgba(0,0,0,0.5)" }}>
            <button onClick={() => doExport()}
              style={{ width:"100%", background:"none", border:"none", color:C.text, fontSize:13, padding:"10px 12px",
                cursor:"pointer", textAlign:"left", borderRadius:8, display:"flex", alignItems:"center", gap:8 }}
              onMouseOver={e=>e.currentTarget.style.background=C.surface2}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              📤 Export Backup
            </button>
            <button onClick={() => fileRef.current?.click()}
              style={{ width:"100%", background:"none", border:"none", color:C.text, fontSize:13, padding:"10px 12px",
                cursor:"pointer", textAlign:"left", borderRadius:8, display:"flex", alignItems:"center", gap:8 }}
              onMouseOver={e=>e.currentTarget.style.background=C.surface2}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              📥 {importing ? "Importing…" : "Import Backup"}
            </button>
            <input ref={fileRef} type="file" accept=".json" onChange={doImport} style={{ display:"none" }} />
            <div style={{ height:1, background:C.border, margin:"4px 0" }} />
            <button onClick={() => {
                if (!window.confirm("Reset ALL data and load Ahmed's demo? This cannot be undone.")) return;
                Object.keys(localStorage).filter(k => k.startsWith("bb_")).forEach(k => localStorage.removeItem(k));
                localStorage.setItem("bb_load_demo_on_start", "1");
                window.location.reload();
              }}
              style={{ width:"100%", background:"none", border:"none", color:C.amber, fontSize:13, padding:"10px 12px",
                cursor:"pointer", textAlign:"left", borderRadius:8, display:"flex", alignItems:"center", gap:8 }}
              onMouseOver={e=>e.currentTarget.style.background="rgba(245,166,35,0.08)"}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              ✨ Load Demo Data
            </button>
            <button onClick={() => {
                if (!window.confirm("Delete ALL your data permanently? This cannot be undone.")) return;
                Object.keys(localStorage).filter(k => k.startsWith("bb_")).forEach(k => localStorage.removeItem(k));
                window.location.reload();
              }}
              style={{ width:"100%", background:"none", border:"none", color:C.red, fontSize:13, padding:"10px 12px",
                cursor:"pointer", textAlign:"left", borderRadius:8, display:"flex", alignItems:"center", gap:8 }}
              onMouseOver={e=>e.currentTarget.style.background="rgba(240,82,82,0.08)"}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              🗑️ Reset All Data
            </button>

            <div style={{ height:1, background:C.border, margin:"4px 0" }} />

            {/* Admin unlock — hidden, no label */}
            {!showAdminInput ? (
              <button onClick={() => setShowAdminInput(true)}
                style={{ width:"100%", background:"none", border:"none", color:C.text3,
                  fontSize:11, padding:"8px 12px", cursor:"pointer", textAlign:"left",
                  borderRadius:8, letterSpacing:"0.05em" }}>
                ···
              </button>
            ) : (
              <div style={{ padding:"8px 12px" }}>
                <input
                  autoFocus
                  type="password"
                  placeholder="Admin code"
                  value={adminCode}
                  onChange={e => setAdminCode(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      if (adminCode === process.env.REACT_APP_ADMIN_CODE || adminCode === "barakah-admin-2026") {
                        if (isPro()) {
                          localStorage.removeItem("bb_pro_token");
                          localStorage.removeItem("bb_pro_verified_at");
                          localStorage.setItem("bb_pro", "false");
                        } else {
                          localStorage.setItem("bb_pro_token", "admin-local-token");
                          localStorage.setItem("bb_pro", "true");
                          localStorage.setItem("bb_pro_verified_at", Date.now().toString());
                        }
                        window.location.reload();
                      } else {
                        setAdminCode("");
                        setShowAdminInput(false);
                      }
                    }
                    if (e.key === "Escape") { setShowAdminInput(false); setAdminCode(""); }
                  }}
                  style={{ width:"100%", background:C.surface2, border:`1px solid ${C.border2}`,
                    borderRadius:8, padding:"8px 10px", color:C.text, fontSize:13,
                    fontFamily:"'DM Sans',sans-serif", outline:"none" }}
                />
                <div style={{ color:C.text3, fontSize:10, marginTop:4 }}>
                  {isPro() ? "Currently: PRO — enter code to switch to Free" : "Currently: Free — enter code to switch to Pro"}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────

export { Onboarding, ExportImport };